<template>
	<div class="charts-wrapper">
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('total_covers')"
			:caption="getString('total')"
			:value="coverTotal"
			:last-period-value="coverTotalPrevious"
			:colors="['#dadada', '#0752A3']"
			:series="coverSalesSeries"
			:xaxis="xaxisLabels"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('covers_num')"
			:caption="getString('covers')"
			:value="coverTotalNum"
			:last-period-value="coverTotalNumPrevious"
			:colors="['#dadada', '#0752A3']"
			:series="coverNumSeries"
			:xaxis="xaxisLabels"
			no-currency
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('tables_revenue')"
			:caption="bestTableName"
			:value="bestTableRevenue"
			:last-period-value="bestTableRevenuePrevious"
			:colors="analyticsStore.bestColors"
			:series="tableRevenuesSeries"
			:xaxis="xaxisLabels"
			type="area"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('tables_orders')"
			:caption="bestTableNameOrders"
			:value="bestTableOrders"
			:last-period-value="bestTableOrdersPrevious"
			:colors="analyticsStore.bestColors"
			:series="tableOrdersSeries"
			:xaxis="xaxisLabels"
			type="area"
			no-currency
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('rooms_revenue')"
			:caption="bestRoomName"
			:value="bestRoomRevenue"
			:last-period-value="bestRoomRevenuePrevious"
			:colors="analyticsStore.bestColors"
			:series="roomRevenuesSeries"
			:xaxis="xaxisLabels"
			type="area"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('rooms_orders')"
			:caption="bestRoomNameOrders"
			:value="bestRoomOrders"
			:last-period-value="bestRoomOrdersPrevious"
			:colors="analyticsStore.bestColors"
			:series="roomOrdersSeries"
			:xaxis="xaxisLabels"
			type="area"
			no-currency
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('tables_visits')"
			:caption="bestTableVisitsName"
			:value="bestTableVisits"
			:last-period-value="bestTableVisitsPrevious"
			:colors="analyticsStore.bestColors"
			:series="tableVisitsSeries"
			:xaxis="xaxisLabels"
			type="area"
			no-currency
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('rooms_visits')"
			:caption="bestRoomVisitsName"
			:value="bestRoomVisits"
			:last-period-value="bestRoomVisitsPrevious"
			:colors="analyticsStore.bestColors"
			:series="roomVisitsSeries"
			:xaxis="xaxisLabels"
			type="area"
			no-currency
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
	</div>
</template>

<script>
import AppChart from "@/shared/components/AppChart.vue";
import {useAnalyticsStore, useMenuStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {useUserStore} from "@/user/stores";

export default {
	setup() {
		const analyticsStore = useAnalyticsStore();
		const languageStore = useLanguageStore();
		const menuStore = useMenuStore();
		const currencyStore = useCurrencyStore();
		const userStore = useUserStore();
		return {menuStore, analyticsStore, currencyStore, languageStore, getString: languageStore.getString, userStore};
	},
	name: "TablesChartsAggregated",
	components: {AppChart},
	mixins: [WindowWidth],
	props: {
		dates: {
			type: Object,
			required: true,
			validator: (value) => {
				return value.hasOwnProperty("start") && value.hasOwnProperty("end") && value.hasOwnProperty("previousStart") && value.hasOwnProperty("previousEnd") && value.hasOwnProperty("timeframeIndex");
			}
		},
	},
	computed: {
		selectorsRowHeight() {
			return this.windowWidth > 820 ? 80 : this.windowWidth > 420 ? 150 : 190;
		},
		aggregatedData() {
			return this.analyticsStore.tablesAggregated || {};
		},
		xaxisLabels() {
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);
			return xaxisTimes.map(time => time.label);
		},
		adjustedDates() {
			if (this.dates.timeframeIndex === 2 || this.dates.timeframeIndex === 6) {
				const now = new Date();
				let adjustedStart, adjustedEnd, adjustedPreviousStart, adjustedPreviousEnd;
				
				if (this.dates.timeframeIndex === 2) {
					adjustedStart = this.analyticsStore.firstDayOfCurrentMonth();
					adjustedEnd = now;
					const elapsedTime = adjustedEnd.getTime() - adjustedStart.getTime();
					adjustedPreviousStart = this.analyticsStore.firstDayOfLastMonth();
					adjustedPreviousEnd = new Date(adjustedPreviousStart.getTime() + elapsedTime);
				} else {
					adjustedStart = this.analyticsStore.firstDayOfCurrentYear();
					adjustedEnd = now;
					const elapsedTime = adjustedEnd.getTime() - adjustedStart.getTime();
					adjustedPreviousStart = this.analyticsStore.firstDayOfLastYear();
					adjustedPreviousEnd = new Date(adjustedPreviousStart.getTime() + elapsedTime);
				}
				
				return {
					...this.dates,
					start: adjustedStart,
					end: adjustedEnd,
					previousStart: adjustedPreviousStart,
					previousEnd: adjustedPreviousEnd
				};
			}
			return this.dates;
		},
		coverTotal() {
			return this.calculateTotal('tables_orders', 'cover_total', false);
		},
		coverTotalPrevious() {
			return this.calculateTotal('tables_orders', 'cover_total', true);
		},
		coverTotalNum() {
			return this.calculateTotal('tables_orders', 'cover_cnt', false);
		},
		coverTotalNumPrevious() {
			return this.calculateTotal('tables_orders', 'cover_cnt', true);
		},
		coverSalesSeries() {
			const previousData = this.buildTimeSeries('tables_orders', 'cover_total', true);
			const currentData = this.buildTimeSeries('tables_orders', 'cover_total', false);
			
			// Check if all values are 0
			const hasData = previousData.some(v => v > 0) || currentData.some(v => v > 0);
			if (!hasData) {
				return [];
			}
			
			return [
				{
					name: this.getString('previous_period'),
					data: previousData
				},
				{
					name: this.getString('current_period'),
					data: currentData
				}
			];
		},
		coverNumSeries() {
			const previousData = this.buildTimeSeries('tables_orders', 'cover_cnt', true);
			const currentData = this.buildTimeSeries('tables_orders', 'cover_cnt', false);
			
			// Check if all values are 0
			const hasData = previousData.some(v => v > 0) || currentData.some(v => v > 0);
			if (!hasData) {
				return [];
			}
			
			return [
				{
					name: this.getString('previous_period'),
					data: previousData
				},
				{
					name: this.getString('current_period'),
					data: currentData
				}
			];
		},
		bestTableName() {
			const best = this.getBestItem('tables_orders', 'gross_sales', false);
			return best ? best.name : '';
		},
		bestTableRevenue() {
			const best = this.getBestItem('tables_orders', 'gross_sales', false);
			return best ? best.total : 0;
		},
		bestTableRevenuePrevious() {
			const best = this.getBestItem('tables_orders', 'gross_sales', false);
			return best ? this.getItemTotal('tables_orders', best.id, 'gross_sales', true) : 0;
		},
		bestTableNameOrders() {
			const best = this.getBestItem('tables_orders', 'orders_cnt', false);
			return best ? best.name : '';
		},
		bestTableOrders() {
			const best = this.getBestItem('tables_orders', 'orders_cnt', false);
			return best ? best.total : 0;
		},
		bestTableOrdersPrevious() {
			const best = this.getBestItem('tables_orders', 'orders_cnt', false);
			return best ? this.getItemTotal('tables_orders', best.id, 'orders_cnt', true) : 0;
		},
		bestRoomName() {
			const best = this.getBestItem('rooms_orders', 'gross_sales', false);
			return best ? best.name : '';
		},
		bestRoomRevenue() {
			const best = this.getBestItem('rooms_orders', 'gross_sales', false);
			return best ? best.total : 0;
		},
		bestRoomRevenuePrevious() {
			const best = this.getBestItem('rooms_orders', 'gross_sales', false);
			return best ? this.getItemTotal('rooms_orders', best.id, 'gross_sales', true) : 0;
		},
		bestRoomNameOrders() {
			const best = this.getBestItem('rooms_orders', 'orders_cnt', false);
			return best ? best.name : '';
		},
		bestRoomOrders() {
			const best = this.getBestItem('rooms_orders', 'orders_cnt', false);
			return best ? best.total : 0;
		},
		bestRoomOrdersPrevious() {
			const best = this.getBestItem('rooms_orders', 'orders_cnt', false);
			return best ? this.getItemTotal('rooms_orders', best.id, 'orders_cnt', true) : 0;
		},
		bestTableVisitsName() {
			const best = this.getBestItem('tables_visits', 'visits_count', false);
			return best ? best.name : '';
		},
		bestTableVisits() {
			const best = this.getBestItem('tables_visits', 'visits_count', false);
			return best ? best.total : 0;
		},
		bestTableVisitsPrevious() {
			const best = this.getBestItem('tables_visits', 'visits_count', false);
			return best ? this.getItemTotal('tables_visits', best.id, 'visits_count', true) : 0;
		},
		bestRoomVisitsName() {
			const best = this.getBestItem('rooms_visits', 'visits_count', false);
			return best ? best.name : '';
		},
		bestRoomVisits() {
			const best = this.getBestItem('rooms_visits', 'visits_count', false);
			return best ? best.total : 0;
		},
		bestRoomVisitsPrevious() {
			const best = this.getBestItem('rooms_visits', 'visits_count', false);
			return best ? this.getItemTotal('rooms_visits', best.id, 'visits_count', true) : 0;
		},
		tableRevenuesSeries() {
			return this.buildDimensionSeries('tables_orders', 'gross_sales', 'room_table_id');
		},
		tableOrdersSeries() {
			return this.buildDimensionSeries('tables_orders', 'orders_cnt', 'room_table_id');
		},
		roomRevenuesSeries() {
			return this.buildDimensionSeries('rooms_orders', 'gross_sales', 'room_id');
		},
		roomOrdersSeries() {
			return this.buildDimensionSeries('rooms_orders', 'orders_cnt', 'room_id');
		},
		tableVisitsSeries() {
			return this.buildDimensionSeries('tables_visits', 'visits_count', 'room_table_id');
		},
		roomVisitsSeries() {
			return this.buildDimensionSeries('rooms_visits', 'visits_count', 'room_id');
		},
	},
	methods: {
		calculateTotal(dataKey, field, isPrevious) {
			if (!this.aggregatedData[dataKey]) return 0;
			
			const bucketColumn = this.getBucketColumn(dataKey);
			const startDate = isPrevious ? this.adjustedDates.previousStart : this.adjustedDates.start;
			const endDate = isPrevious ? this.adjustedDates.previousEnd : this.adjustedDates.end;
			
			return this.aggregatedData[dataKey]
				.filter(row => {
					const date = new Date(row[bucketColumn]);
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(endDate);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						return date >= startDate && date <= adjustedEndDate;
					}
					return date >= startDate && date <= endDate;
				})
				.reduce((sum, row) => {
					const value = row[field] || 0;
					// Convert cents to actual value for monetary fields
					return sum + (field.includes('total') || field.includes('sales') ? value / 100 : value);
				}, 0);
		},
		buildTimeSeries(dataKey, field, isPrevious) {
			if (!this.aggregatedData[dataKey]) return [];
			
			const bucketColumn = this.getBucketColumn(dataKey);
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);
			const startDate = isPrevious ? this.adjustedDates.previousStart : this.adjustedDates.start;
			const endDate = isPrevious ? this.adjustedDates.previousEnd : this.adjustedDates.end;
			
			const series = new Array(xaxisTimes.length).fill(0);
			
			this.aggregatedData[dataKey]
				.filter(row => {
					const date = new Date(row[bucketColumn]);
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(endDate);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						return date >= startDate && date <= adjustedEndDate;
					}
					return date >= startDate && date <= endDate;
				})
				.forEach(row => {
					const bucketDate = new Date(row[bucketColumn]);
					
					// Align to hour boundaries for non-hour buckets (like Orders does)
					if (xaxisTimes.length > 0 && bucketColumn !== 'hour_bucket') {
						bucketDate.setHours(xaxisTimes[0].rawDate.getHours(), xaxisTimes[0].rawDate.getMinutes(), 0, 0);
					}
					
					const mockOrder = { date: bucketDate };
					const index = this.analyticsStore.getDatesIndex(this.adjustedDates, xaxisTimes, mockOrder, isPrevious);
					
					if (index >= 0 && index < series.length) {
						const value = Number(row[field] || 0);
						const isMonetaryField = field.includes('total') || field.includes('sales');
						series[index] += isMonetaryField ? value / 100 : value;
					}
				});
			
			return series;
		},
		buildDimensionSeries(dataKey, field, idField) {
			if (!this.aggregatedData[dataKey]) return [];
			
			// Get top items in current period
			const items = this.getTopItems(dataKey, field, false, 6);
			
			const series = items.map((item, index) => ({
				name: item.name,
				color: this.analyticsStore.bestColors[index],
				data: this.buildItemTimeSeries(dataKey, field, idField, item.id, false)
			}));
			
			// Check if all series have only zero values
			const hasData = series.some(s => s.data && s.data.some(v => v > 0));
			if (!hasData) {
				return [];
			}
			
			return series;
		},
		getBestItem(dataKey, field, isPrevious) {
			const items = this.getTopItems(dataKey, field, isPrevious, 1);
			return items.length > 0 ? items[0] : null;
		},
		getTopItems(dataKey, field, isPrevious, limit) {
			if (!this.aggregatedData[dataKey]) return [];
			
			const totals = new Map();
			const names = new Map();
			const idField = dataKey.includes('tables') ? 'room_table_id' : 'room_id';
			const bucketColumn = this.getBucketColumn(dataKey);
			const startDate = isPrevious ? this.adjustedDates.previousStart : this.adjustedDates.start;
			const endDate = isPrevious ? this.adjustedDates.previousEnd : this.adjustedDates.end;
			
			this.aggregatedData[dataKey]
				.filter(row => {
					const date = new Date(row[bucketColumn]);
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(endDate);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						return date >= startDate && date <= adjustedEndDate;
					}
					return date >= startDate && date <= endDate;
				})
				.forEach(row => {
					const id = row[idField];
					if (id) {
						const value = row[field] || 0;
						// Convert cents to actual value for monetary fields
						const actualValue = field.includes('total') || field.includes('sales') ? value / 100 : value;
						totals.set(id, (totals.get(id) || 0) + actualValue);
						
						// Store name from metadata
						if (!names.has(id)) {
							if (dataKey.includes('tables')) {
								const table = this.aggregatedData.tables_metadata?.find(t => t.room_table_id == id);
								names.set(id, table?.table_name || `Table ${id}`);
							} else {
								const room = this.aggregatedData.rooms_metadata?.find(r => r.room_id == id);
								names.set(id, room?.name || `Room ${id}`);
							}
						}
					}
				});
			
			return Array.from(totals.entries())
				.map(([id, total]) => ({ id, total, name: names.get(id) || '' }))
				.sort((a, b) => b.total - a.total)
				.slice(0, limit);
		},
		getItemTotal(dataKey, itemId, field, isPrevious) {
			if (!this.aggregatedData[dataKey]) return 0;
			
			const idField = dataKey.includes('tables') ? 'room_table_id' : 'room_id';
			const bucketColumn = this.getBucketColumn(dataKey);
			const startDate = isPrevious ? this.adjustedDates.previousStart : this.adjustedDates.start;
			const endDate = isPrevious ? this.adjustedDates.previousEnd : this.adjustedDates.end;
			
			return this.aggregatedData[dataKey]
				.filter(row => {
					const date = new Date(row[bucketColumn]);
					let dateMatch;
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(endDate);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						dateMatch = date >= startDate && date <= adjustedEndDate;
					} else {
						dateMatch = date >= startDate && date <= endDate;
					}
					return dateMatch && row[idField] == itemId;
				})
				.reduce((sum, row) => {
					const value = row[field] || 0;
					// Convert cents to actual value for monetary fields
					return sum + (field.includes('total') || field.includes('sales') ? value / 100 : value);
				}, 0);
		},
		buildItemTimeSeries(dataKey, field, idField, itemId, isPrevious) {
			if (!this.aggregatedData[dataKey]) return [];
			
			const bucketColumn = this.getBucketColumn(dataKey);
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);
			const startDate = isPrevious ? this.adjustedDates.previousStart : this.adjustedDates.start;
			const endDate = isPrevious ? this.adjustedDates.previousEnd : this.adjustedDates.end;
			
			const series = new Array(xaxisTimes.length).fill(0);
			
			this.aggregatedData[dataKey]
				.filter(row => {
					const date = new Date(row[bucketColumn]);
					let dateMatch;
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(endDate);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						dateMatch = date >= startDate && date <= adjustedEndDate;
					} else {
						dateMatch = date >= startDate && date <= endDate;
					}
					return dateMatch && row[idField] == itemId;
				})
				.forEach(row => {
					const bucketDate = new Date(row[bucketColumn]);
					
					// Align to hour boundaries for non-hour buckets (like Orders does)
					if (xaxisTimes.length > 0 && bucketColumn !== 'hour_bucket') {
						bucketDate.setHours(xaxisTimes[0].rawDate.getHours(), xaxisTimes[0].rawDate.getMinutes(), 0, 0);
					}
					
					const mockOrder = { date: bucketDate };
					const index = this.analyticsStore.getDatesIndex(this.adjustedDates, xaxisTimes, mockOrder, isPrevious);
					
					if (index >= 0 && index < series.length) {
						const value = Number(row[field] || 0);
						const isMonetaryField = field.includes('total') || field.includes('sales');
						series[index] += isMonetaryField ? value / 100 : value;
					}
				});
			
			return series;
		},
		getBucketColumn(dataKey) {
			// Use the bucket column from the aggregated data response
			return this.aggregatedData.bucketColumn || 'day_bucket';
		}
	},
}
</script>

<style lang="scss" scoped>
.charts-wrapper {
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;
	width: 100%;
	margin-bottom: calc(-1 * var(--main-padding));

	.chart-container {
		min-width: calc((100% - var(--main-padding)) / 2);
		max-width: calc((100% - var(--main-padding)) / 2);
		margin-bottom: var(--main-padding);

		&:deep(.chart-svg-container) {
			transition: min-height .35s ease-in-out;
			height: 200px;
		}

		&.expanded {
			min-width: 100%;
			max-width: 100%;

			&:deep(.chart-svg-container) {
				height: 350px;
			}
		}


		&:deep(.card-header h3) {
			font-size: 18px;
		}

	}

	@media (max-width: 920px) {
		justify-content: space-between;
		.chart-container {
			max-width: calc((100% - var(--main-padding)) / 2);

			&.expanded {
				max-width: 100%;
			}
		}
	}

	@media (max-width: 670px) {
		justify-content: center;
		.chart-container {
			min-width: unset;
			max-width: 100%;
			width: 100%;

			&.expanded {
				max-width: 100%;
			}
		}
	}
}
</style>